import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation } from '@tanstack/react-query';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  FormField,
  Input,
  Spinner,
} from '@shared/ui';
import { passwordResetApi } from '@features/auth';
import { useSessionStore } from '@entities/session';

const schema = z
  .object({
    currentPassword: z.string().min(1),
    newPassword: z.string().min(8),
    confirm: z.string(),
  })
  .refine((d) => d.newPassword === d.confirm, {
    path: ['confirm'],
    message: 'Passwords do not match',
  });

type Values = z.infer<typeof schema>;

export function SettingsPage() {
  const identity = useSessionStore((s) => s.identity);
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<Values>({ resolver: zodResolver(schema) });

  const change = useMutation({
    mutationFn: (v: Values) => passwordResetApi.change(v.currentPassword, v.newPassword),
    onSuccess: () => reset(),
  });

  return (
    <div className="max-w-xl space-y-6">
      <h1 className="text-2xl font-semibold tracking-tight">Настройки</h1>

      <Card>
        <CardHeader>
          <CardTitle>Аккаунт</CardTitle>
        </CardHeader>
        <CardContent className="space-y-1 text-sm">
          <div>
            <span className="text-muted-foreground">Email: </span>
            {identity?.email ?? '—'}
          </div>
          <div>
            <span className="text-muted-foreground">Display name: </span>
            {identity?.displayName ?? '—'}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Смена пароля</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit((v) => change.mutate(v))} className="space-y-4">
            <FormField label="Текущий пароль" htmlFor="cur" error={errors.currentPassword?.message}>
              <Input id="cur" type="password" {...register('currentPassword')} />
            </FormField>
            <FormField label="Новый пароль" htmlFor="new" error={errors.newPassword?.message}>
              <Input id="new" type="password" {...register('newPassword')} />
            </FormField>
            <FormField label="Подтверждение" htmlFor="conf" error={errors.confirm?.message}>
              <Input id="conf" type="password" {...register('confirm')} />
            </FormField>
            <Button type="submit" disabled={change.isPending}>
              {change.isPending ? <Spinner /> : 'Сохранить'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
